/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useRef, useState } from "react";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { Panel, PANEL_STYLE } from "../../../../ui/components/Panel";
import { IntegrationEditorEdit } from "../IntegrationEditorEdit";
import { InspectorContextProvider } from "../../../../inspector/InspectorContext";
import { Inspector } from "../../../../inspector";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { TGenericModelNode } from "@adapptio/lib-blueprint";
import {
	InspectPanel,
	InspectPanelContent,
	InspectPanelHeader,
	InspectPanelHeaderInfo,
	InspectPanelHeaderInfoTitle
} from "../../../../ui/components/InspectPanel";
import { DEFAULT_ENV_NAME } from "../../Common/IntegrationModel";

export interface IIntegrationEditorContentProps {
	className?: string;
	view: IntegrationEditorEdit;
}

export const IntegrationEditorContent: React.FC<IIntegrationEditorContentProps> = ({
	className,
	view
}) => {
	const baseClass = BEM("edt-integration-editor-content");
	const t = useTranslate();

	const inspectorElRef = useRef();
	const model = view.getIntegrationModel();

	const [, setForcedRender] = useState(0);

	if (className) {
		baseClass.add(className);
	}

	const forceRender = useCallback(() => {
		setForcedRender((value) => (value += 1));
	}, []);

	const getModelOptsForEnv = useCallback<() => TGenericModelNode>(() => {
		return model.getOptsForEnvironment(DEFAULT_ENV_NAME, { preventCreate: true });
	}, [model]);

	useSimpleEvent(
		model.onModelChange,
		() => {
			forceRender();
		},
		[model]
	);

	const envModelOpts = getModelOptsForEnv();

	return (
		<Panel style={PANEL_STYLE.PRIMARY} scroll className={baseClass.toClassName()}>
			<div className={baseClass.element("wrapper").toClassName()}>
				<div
					className={baseClass.element("inspector").toClassName()}
					ref={inspectorElRef}
				>
					<InspectorContextProvider inspectorElRef={inspectorElRef}>
						<InspectPanel>
							<InspectPanelHeader>
								<InspectPanelHeaderInfo>
									<InspectPanelHeaderInfoTitle>
										{t("editor", "Integration Settings")}
									</InspectPanelHeaderInfoTitle>
								</InspectPanelHeaderInfo>
							</InspectPanelHeader>
							<InspectPanelContent>
								<Inspector model={envModelOpts} />
							</InspectPanelContent>
						</InspectPanel>
					</InspectorContextProvider>
				</div>
			</div>
		</Panel>
	);
};
